import { IcbtTasks } from "api/types";
import { isFuture, isPast, isThisWeek, isToday } from "date-fns";
import { useMemo } from "react";
import { filter, map, partition, pipe, sortBy } from "remeda";

export const categoriesForTasks = ["now", "thisWeek", "all"] as const;
export type CategoriesForTasks = (typeof categoriesForTasks)[number];

type BasePatientData = { userId: number };

export type IcbtTasksWithPatientData<PatientData extends BasePatientData> =
  IcbtTasks & {
    patient?: PatientData;
  };

// const now = new Date();

export const useSortedIcbtTodoTasks = <PatientData extends BasePatientData>({
  icbtTasks,
  patientsToIncludeInCommunication: patients,
}: {
  icbtTasks?: IcbtTasks[];
  patientsToIncludeInCommunication?: PatientData[];
}) =>
  useMemo(() => {
    if (!icbtTasks)
      return new Map(
        Object.entries({
          now: [],
          thisWeek: [],
          all: [],
        }),
      );
    const sorted = pipe(
      icbtTasks,
      filter((request) => {
        // checking for future only removes requests that are sorted to done as they are too old to have the task "extend_due_date", and are also missing feedback.
        const isFutureNonFeedbackedRequest =
          !request.feedbackPublishedAt &&
          !request.feedbackDateIgnored &&
          isFuture(new Date(request.feedbackDate));
        return (
          Boolean(request.feedbackDate) &&
          (request.tasks.length > 0 ||
            request.status === "todo" ||
            isFutureNonFeedbackedRequest)
        );
      }),
      // Include patient data
      map((request) => {
        return {
          ...request,
          patient: patients?.find(
            (p) => p.userId === Number(request.patientUserId),
          ),
        };
      }),
      sortBy(
        [
          (request) =>
            request.tasks.some(
              (thisTask) => thisTask.type === "extend_due_date",
            )
              ? 0
              : 1,
          "desc",
        ],
        (request) => new Date(request.feedbackDate),
      ),
      // add check on today/past OR have new response, not just tasks
      partition((request) => {
        const feedbackDate = new Date(request.feedbackDate);
        const isNowTask =
          request.tasks.length > 0 &&
          (isToday(feedbackDate) || isPast(feedbackDate));
        const isUnansweredResponse =
          new Date(request.patientResponsePublishedAt ?? 0) >
          new Date(request.feedbackPublishedAt ?? 0);

        return isNowTask || isUnansweredResponse;
      }),
      // partition the rest to week and final rest
      ([now, rest]) => [
        now,
        ...partition(rest, (request) =>
          isThisWeek(new Date(request.feedbackDate), {
            weekStartsOn: 1, // Monday
          }),
        ),
      ],
      ([now, thisWeek, rest]) => [
        now,
        [...now, ...thisWeek],
        [...now, ...thisWeek, ...rest],
      ],
      // Give the categories names.
      ([now, thisWeek, all]) =>
        new Map<CategoriesForTasks, IcbtTasksWithPatientData<PatientData>[]>(
          Object.entries({
            now,
            thisWeek: sortBy(
              thisWeek,
              (request) => new Date(request.feedbackDate),
            ),
            all: sortBy(all, (request) => new Date(request.feedbackDate)),
          } as const) as [
            CategoriesForTasks,
            IcbtTasksWithPatientData<PatientData>[],
          ][],
        ),
    );
    return sorted;
  }, [patients, icbtTasks]);
